import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { listOpportunities, getOpportunityFilters } from "@/api/db";
import { db } from "@/api/db";
import {
  Briefcase, Search, ChevronRight, ChevronLeft, X,
  DollarSign, MapPin, Building2, Clock, FileText, SlidersHorizontal,
  Check, User, Star
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value) {
  if (!value) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

// State name → abbreviation mapping
const STATE_ABBR = {
  "Acre": "AC", "Alagoas": "AL", "Amapá": "AP", "Amazonas": "AM",
  "Bahia": "BA", "Ceará": "CE", "Distrito Federal": "DF", "Espírito Santo": "ES",
  "Goiás": "GO", "Maranhão": "MA", "Mato Grosso": "MT", "Mato Grosso do Sul": "MS",
  "Minas Gerais": "MG", "Pará": "PA", "Paraíba": "PB", "Paraná": "PR",
  "Pernambuco": "PE", "Piauí": "PI", "Rio de Janeiro": "RJ", "Rio Grande do Norte": "RN",
  "Rio Grande do Sul": "RS", "Rondônia": "RO", "Roraima": "RR", "Santa Catarina": "SC",
  "São Paulo": "SP", "Sergipe": "SE", "Tocantins": "TO",
};

function getStateAbbreviation(state) {
  if (!state) return null;
  // Direct match
  if (STATE_ABBR[state]) return STATE_ABBR[state];
  // Case-insensitive match
  const normalized = state.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  for (const [name, abbr] of Object.entries(STATE_ABBR)) {
    const nameNorm = name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (nameNorm === normalized) return abbr;
  }
  // If already an abbreviation
  if (state.length === 2) return state.toUpperCase();
  return state;
}

function formatCityName(city) {
  if (!city) return "";
  const lowerCaseWords = ["de", "da", "das", "do", "dos", "e"];
  return city.toLowerCase().split(" ").map((word, i) =>
    lowerCaseWords.includes(word) && i !== 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
  ).join(" ");
}

// ─── Company Logo ─────────────────────────────────────────────────────────────

function CompanyLogo({ logoUrl, name, isConfidential, size = 70 }) {
  const [imgError, setImgError] = useState(false);
  if (logoUrl && !isConfidential && !imgError) {
    return (
      <img src={logoUrl} alt={name} onError={() => setImgError(true)}
        className="rounded-full object-contain bg-white flex-shrink-0"
        style={{ width: size, height: size, border: "1px solid #E5E9EA" }} />
    );
  }
  return (
    <div className="rounded-full flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size, background: "#F2F4F4" }}>
      <Building2 className="w-8 h-8" style={{ color: "#99A6AB" }} />
    </div>
  );
}

// ─── Job Card ─────────────────────────────────────────────────────────────────

function JobCard({ job }) {
  const minSalary = formatCurrency(job.minSalary);
  const maxSalary = formatCurrency(job.maxSalary);

  const jobPositionWithSeniority = job.seniority
    ? `${job.title} ${job.seniority}`
    : job.title;

  const locationStr = job.location?.city && job.location?.state
    ? `${formatCityName(job.location.city)}, ${getStateAbbreviation(job.location.state)}`
    : "Todas as regiões";

  const iconColor = "#2E3233";

  return (
    <div className="rounded-3xl flex flex-col justify-between h-full"
      style={{ background: "rgba(255,255,255,0.7)", border: "1px solid #e2e8f0" }}>
      <div className="flex flex-col gap-4 p-5 md:p-6 h-full">
        {/* Logo + Title + Company */}
        <div className="flex gap-4 items-center">
          <CompanyLogo logoUrl={job.logoUrl} name={job.companyName} isConfidential={job.isConfidential} size={70} />
          <div className="flex flex-col gap-0.5 min-w-0" style={{ width: "70%" }}>
            <h3 className="font-bold text-sm leading-snug line-clamp-2" style={{ color: "#2E3233" }}>
              {jobPositionWithSeniority}
            </h3>
            <p className="text-sm truncate" style={{ color: "#646C6F" }}>
              {job.isConfidential ? "Empresa confidencial" : job.companyName}
            </p>
          </div>
        </div>

        {/* Contract model */}
        {job.contractModel && (
          <div className="flex items-center gap-2 overflow-hidden">
            <Briefcase className="w-5 h-5 flex-shrink-0" style={{ color: iconColor }} />
            <span className="text-sm truncate" style={{ color: "#646C6F" }}>{job.contractModel}</span>
          </div>
        )}

        {/* Salary */}
        <div className="flex items-center gap-2 overflow-hidden">
          <DollarSign className="w-5 h-5 flex-shrink-0" style={{ color: iconColor }} />
          <span className="text-sm truncate" style={{ color: "#646C6F" }}>
            {minSalary} - {maxSalary}
          </span>
        </div>

        {/* Job model + Availability side by side */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 sm:items-center">
          {job.jobModel && (
            <div className="flex items-center gap-2 sm:w-1/2 overflow-hidden">
              <FileText className="w-5 h-5 flex-shrink-0" style={{ color: iconColor }} />
              <span className="text-sm truncate" style={{ color: "#646C6F" }}>{job.jobModel}</span>
            </div>
          )}
          {job.availability && (
            <div className="flex items-center gap-2 sm:w-1/2 overflow-hidden">
              <Clock className="w-5 h-5 flex-shrink-0" style={{ color: iconColor }} />
              <span className="text-sm truncate" style={{ color: "#646C6F" }}>{job.availability}</span>
            </div>
          )}
        </div>

        {/* Location */}
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 flex-shrink-0" style={{ color: iconColor }} />
          <span className="text-sm truncate" style={{ color: "#646C6F" }}>{locationStr}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 md:px-6 pb-5 md:pb-6">
        <Link
          to={createPageUrl("OpportunityDetail") + `?id=${job.id}`}
          className="flex items-center justify-center gap-1 text-sm font-medium transition-opacity hover:opacity-70"
          style={{ color: "#00664C" }}
        >
          Acessar detalhes <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

// ─── Skeleton Card ───────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="rounded-3xl p-5 md:p-6 animate-pulse" style={{ background: "rgba(255,255,255,0.7)", border: "1px solid #e2e8f0", height: 380 }}>
      <div className="flex gap-4 items-center mb-6">
        <div className="w-[70px] h-[70px] rounded-full" style={{ background: "#E5E9EA" }} />
        <div className="flex-1 space-y-2" style={{ width: "70%" }}>
          <div className="h-5 rounded-sm w-full" style={{ background: "#E5E9EA" }} />
          <div className="h-4 rounded-sm w-3/5" style={{ background: "#E5E9EA" }} />
        </div>
      </div>
      <div className="space-y-6">
        <div className="h-4 rounded-sm w-1/4" style={{ background: "#E5E9EA" }} />
        <div className="h-4 rounded-sm w-full" style={{ background: "#E5E9EA" }} />
        <div className="flex gap-6">
          <div className="h-4 rounded-sm w-1/2" style={{ background: "#E5E9EA" }} />
          <div className="h-4 rounded-sm w-1/2" style={{ background: "#E5E9EA" }} />
        </div>
        <div className="h-4 rounded-sm w-full" style={{ background: "#E5E9EA" }} />
      </div>
    </div>
  );
}

// ─── Filter checkbox group ────────────────────────────────────────────────────

function FilterCheckboxGroup({ title, icon, options, selected, onChange, searchable, disabled, disabledMessage }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(term));
  }, [options, searchTerm]);

  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div style={{ borderBottom: "1px solid #E5E9EA" }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-4 px-1"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium" style={{ color: "#2E3233" }}>{title}</span>
          {selected.length > 0 && (
            <span className="text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
              style={{ background: "#00664C", color: "#fff" }}>
              {selected.length}
            </span>
          )}
        </div>
        <ChevronRight
          className="w-4 h-4 transition-transform"
          style={{ color: "#646C6F", transform: open ? "rotate(90deg)" : "none" }}
        />
      </button>

      {open && (
        <div className="pb-4 px-1">
          {disabled && disabledMessage && (
            <p className="text-xs py-2 px-1 mb-2" style={{ color: "#99A6AB" }}>{disabledMessage}</p>
          )}
          {searchable && !disabled && options.length > 6 && (
            <div className="mb-3 rounded-xl flex items-center gap-2 px-3 py-2"
              style={{ border: "1px solid #E5E9EA" }}>
              <Search className="w-3.5 h-3.5" style={{ color: "#99A6AB" }} />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="flex-1 text-sm bg-transparent outline-none"
                style={{ color: "#2E3233" }}
              />
            </div>
          )}
          <div className="flex flex-col gap-1 max-h-[240px] overflow-y-auto">
            {filteredOptions.map(opt => {
              const isSelected = selected.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => !disabled && toggle(opt.value)}
                  className="flex items-center gap-2.5 py-2 px-1 rounded-lg text-sm text-left transition-colors"
                  style={{
                    color: disabled ? "#C8CED0" : isSelected ? "#00664C" : "#2E3233",
                    cursor: disabled ? "default" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                  }}
                  onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = "#F2F4F4"; }}
                  onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = "transparent"; }}
                >
                  <div className="rounded flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 18, height: 18,
                      border: isSelected ? "none" : `2px solid ${disabled ? "#E5E9EA" : "#C8CED0"}`,
                      background: isSelected ? (disabled ? "#C8CED0" : "#00664C") : "transparent",
                    }}>
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className="truncate">{opt.label}</span>
                </button>
              );
            })}
            {!disabled && filteredOptions.length === 0 && (
              <p className="text-xs py-2 px-1" style={{ color: "#99A6AB" }}>Nenhum resultado</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Salary range filter ──────────────────────────────────────────────────────

function FilterSalaryRange({ minSalary, maxSalary, onChangeMin, onChangeMax }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ borderBottom: "1px solid #E5E9EA" }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-4 px-1"
      >
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5" style={{ color: "#00664C" }} />
          <span className="text-sm font-medium" style={{ color: "#2E3233" }}>Salário</span>
          {(minSalary || maxSalary) && (
            <span className="text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
              style={{ background: "#00664C", color: "#fff" }}>1</span>
          )}
        </div>
        <ChevronRight
          className="w-4 h-4 transition-transform"
          style={{ color: "#646C6F", transform: open ? "rotate(90deg)" : "none" }}
        />
      </button>

      {open && (
        <div className="pb-4 px-1 flex gap-3">
          <div className="flex-1">
            <label className="text-xs mb-1 block" style={{ color: "#646C6F" }}>Mínimo</label>
            <input
              type="number"
              placeholder="R$ 0"
              value={minSalary}
              onChange={e => onChangeMin(e.target.value)}
              className="w-full rounded-xl px-3 py-2 text-sm outline-none"
              style={{ border: "1px solid #E5E9EA", color: "#2E3233" }}
            />
          </div>
          <div className="flex-1">
            <label className="text-xs mb-1 block" style={{ color: "#646C6F" }}>Máximo</label>
            <input
              type="number"
              placeholder="R$ 50.000"
              value={maxSalary}
              onChange={e => onChangeMax(e.target.value)}
              className="w-full rounded-xl px-3 py-2 text-sm outline-none"
              style={{ border: "1px solid #E5E9EA", color: "#2E3233" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Filter Side Menu ─────────────────────────────────────────────────────────

function FilterSideMenu({ open, onClose, filterOptions, filters, onApply }) {
  const [local, setLocal] = useState(filters);

  // Sync when parent filters change or menu opens
  useEffect(() => {
    if (open) setLocal(filters);
  }, [open]);

  const set = (key, value) => setLocal(prev => ({ ...prev, [key]: value }));

  const filtersCount = useMemo(() => {
    let count = 0;
    if (local.contractModelIds?.length) count += local.contractModelIds.length;
    if (local.jobModelIds?.length) count += local.jobModelIds.length;
    if (local.jobAvailabilityIds?.length) count += local.jobAvailabilityIds.length;
    if (local.jobPositionIds?.length) count += local.jobPositionIds.length;
    if (local.seniorities?.length) count += local.seniorities.length;
    if (local.states?.length) count += local.states.length;
    if (local.cities?.length) count += local.cities.length;
    if (local.minSalary || local.maxSalary) count += 1;
    return count;
  }, [local]);

  // Cities available based on selected states
  const availableCities = useMemo(() => {
    if (!local.states?.length || !filterOptions.citiesByState) return [];
    const cities = [];
    for (const st of local.states) {
      const cits = filterOptions.citiesByState[st] || [];
      for (const c of cits) {
        if (!cities.find(x => x.value === c)) {
          cities.push({ value: c, label: formatCityName(c) });
        }
      }
    }
    return cities.sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [local.states, filterOptions.citiesByState]);

  // Valid city values for current states (for auto-removal)
  const validCityValues = useMemo(() => {
    if (!local.states?.length || !filterOptions.citiesByState) return new Set();
    const valid = new Set();
    for (const st of local.states) {
      for (const c of (filterOptions.citiesByState[st] || [])) {
        valid.add(c);
      }
    }
    return valid;
  }, [local.states, filterOptions.citiesByState]);

  // Auto-remove cities not belonging to selected states
  useEffect(() => {
    const currentCities = local.cities || [];
    if (currentCities.length === 0) return;
    if (!local.states?.length) {
      set("cities", []);
      return;
    }
    const filtered = currentCities.filter(c => validCityValues.has(c));
    if (filtered.length !== currentCities.length) {
      set("cities", filtered);
    }
  }, [local.states, validCityValues]);

  function handleClear() {
    setLocal({
      contractModelIds: [],
      jobModelIds: [],
      jobAvailabilityIds: [],
      jobPositionIds: [],
      seniorities: [],
      states: [],
      cities: [],
      minSalary: "",
      maxSalary: "",
    });
  }

  function handleApply() {
    onApply(local);
    onClose();
  }

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-full sm:max-w-[400px] bg-white z-50 flex flex-col shadow-xl"
        style={{ animation: "slideInRight .25s ease" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid #E5E9EA" }}>
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5" style={{ color: "#00664C" }} />
            <span className="text-base font-bold" style={{ color: "#2E3233" }}>Filtros</span>
            {filtersCount > 0 && (
              <span className="text-xs font-bold rounded-full px-2 py-0.5"
                style={{ background: "#EAFAF6", color: "#00664C" }}>
                {filtersCount}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" style={{ color: "#646C6F" }} />
          </button>
        </div>

        {/* Scrollable filters */}
        <div className="flex-1 overflow-y-auto px-6">
          <FilterCheckboxGroup
            title="Formato de contratação"
            icon={<Briefcase className="w-5 h-5" style={{ color: "#00664C" }} />}
            options={(filterOptions.contractModels || []).map(i => ({ value: i.id, label: i.name }))}
            selected={local.contractModelIds || []}
            onChange={v => set("contractModelIds", v)}
          />

          <FilterCheckboxGroup
            title="Modalidade"
            icon={<FileText className="w-5 h-5" style={{ color: "#00664C" }} />}
            options={(filterOptions.jobModels || []).map(i => ({ value: i.id, label: i.name }))}
            selected={local.jobModelIds || []}
            onChange={v => set("jobModelIds", v)}
          />

          <FilterCheckboxGroup
            title="Disponibilidade"
            icon={<Clock className="w-5 h-5" style={{ color: "#00664C" }} />}
            options={(filterOptions.availabilities || []).map(i => ({ value: i.id, label: i.name }))}
            selected={local.jobAvailabilityIds || []}
            onChange={v => set("jobAvailabilityIds", v)}
          />

          <FilterSalaryRange
            minSalary={local.minSalary || ""}
            maxSalary={local.maxSalary || ""}
            onChangeMin={v => set("minSalary", v)}
            onChangeMax={v => set("maxSalary", v)}
          />

          <FilterCheckboxGroup
            title="Cargo de interesse"
            icon={<Briefcase className="w-5 h-5" style={{ color: "#00664C" }} />}
            options={(filterOptions.jobPositions || []).map(i => ({ value: i.id, label: i.name }))}
            selected={local.jobPositionIds || []}
            onChange={v => set("jobPositionIds", v)}
            searchable
          />

          <FilterCheckboxGroup
            title="Nível de senioridade"
            icon={<Briefcase className="w-5 h-5" style={{ color: "#00664C" }} />}
            options={(filterOptions.seniorities || []).map(i => ({ value: i.value, label: i.label }))}
            selected={local.seniorities || []}
            onChange={v => set("seniorities", v)}
          />

          <FilterCheckboxGroup
            title="Estado"
            icon={<MapPin className="w-5 h-5" style={{ color: "#00664C" }} />}
            options={(filterOptions.states || []).map(s => ({ value: s, label: formatCityName(s) }))}
            selected={local.states || []}
            onChange={v => set("states", v)}
            searchable
          />

          <FilterCheckboxGroup
            title="Cidade"
            icon={<MapPin className="w-5 h-5" style={{ color: "#00664C" }} />}
            options={availableCities}
            selected={local.cities || []}
            onChange={v => set("cities", v)}
            searchable
            disabled={!local.states?.length}
            disabledMessage="Selecione pelo menos um estado para filtrar por cidade"
          />
        </div>

        {/* Footer buttons */}
        <div className="px-6 py-4 flex gap-3" style={{ borderTop: "1px solid #E5E9EA" }}>
          <button
            onClick={handleClear}
            className="flex-1 py-2.5 rounded-full text-sm font-medium transition-colors"
            style={{ border: "1px solid #E5E9EA", color: "#646C6F" }}
          >
            Limpar filtros
          </button>
          <button
            onClick={handleApply}
            className="flex-1 py-2.5 rounded-full text-sm font-medium text-white transition-colors"
            style={{ background: "#00664C" }}
          >
            Aplicar filtros
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Opportunities() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pageSize: 20 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // All filter selections
  const [filters, setFilters] = useState({
    contractModelIds: [],
    jobModelIds: [],
    jobAvailabilityIds: [],
    jobPositionIds: [],
    seniorities: [],
    states: [],
    cities: [],
    minSalary: "",
    maxSalary: "",
  });

  // Filter options from API
  const [filterOptions, setFilterOptions] = useState({
    jobModels: [],
    contractModels: [],
    seniorities: [],
    availabilities: [],
    jobPositions: [],
    states: [],
    citiesByState: {},
  });
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);

  // Fetch filter options once
  useEffect(() => {
    getOpportunityFilters()
      .then(f => setFilterOptions(f))
      .catch(err => console.error("Failed to load filter options:", err))
      .finally(() => setFilterOptionsLoading(false));
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when search changes
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  // Count active filters
  const filtersCount = useMemo(() => {
    let count = 0;
    if (filters.contractModelIds?.length) count += filters.contractModelIds.length;
    if (filters.jobModelIds?.length) count += filters.jobModelIds.length;
    if (filters.jobAvailabilityIds?.length) count += filters.jobAvailabilityIds.length;
    if (filters.jobPositionIds?.length) count += filters.jobPositionIds.length;
    if (filters.seniorities?.length) count += filters.seniorities.length;
    if (filters.states?.length) count += filters.states.length;
    if (filters.cities?.length) count += filters.cities.length;
    if (filters.minSalary || filters.maxSalary) count += 1;
    return count;
  }, [filters]);

  // Fetch data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const apiFilters = {
      page,
      pageSize: 20,
      search: debouncedSearch || undefined,
      jobModelIds: filters.jobModelIds?.length ? filters.jobModelIds : undefined,
      contractModelIds: filters.contractModelIds?.length ? filters.contractModelIds : undefined,
      jobAvailabilityIds: filters.jobAvailabilityIds?.length ? filters.jobAvailabilityIds : undefined,
      jobPositionIds: filters.jobPositionIds?.length ? filters.jobPositionIds : undefined,
      seniorities: filters.seniorities?.length ? filters.seniorities : undefined,
      states: filters.states?.length ? filters.states : undefined,
      cities: filters.cities?.length ? filters.cities : undefined,
      minSalary: filters.minSalary || undefined,
      maxSalary: filters.maxSalary || undefined,
    };
    listOpportunities(apiFilters)
      .then(result => { if (!cancelled) setData(result); })
      .catch(err => console.error("Opportunities load error:", err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, debouncedSearch, filters]);

  function handleApplyFilters(newFilters) {
    setFilters(newFilters);
    setPage(1);
  }

  const totalPages = Math.ceil(data.total / data.pageSize);
  const hasActiveFilters = filtersCount > 0 || debouncedSearch;

  // Profile completeness check + recommended opportunities
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [recommended, setRecommended] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(true);
  const [showAllRecommended, setShowAllRecommended] = useState(false);
  const RECOMMENDED_INITIAL = 6;

  useEffect(() => {
    db.profile.get()
      .then(async (p) => {
        if (!p.addresses || !p.gender || (p.knowledge?.skills?.length === 0)) {
          setProfileIncomplete(true);
        }

        // Build filters from candidate preferences
        const jobPositionIds = (p.preferredPositions || []).map(pos => pos.jobPositionId).filter(Boolean);

        const hasPrefs = jobPositionIds.length > 0;
        if (hasPrefs) {
          try {
            // Try with position + seniority first (best match)
            const seniorities = [...new Set((p.preferredPositions || []).map(pos => pos.seniority).filter(Boolean))];
            const strictFilters = {
              jobPositionIds,
              seniorities: seniorities.length ? seniorities : undefined,
              pageSize: 50,
            };
            const result = await listOpportunities(strictFilters);

            if ((result.items || []).length > 0) {
              setRecommended(result.items);
            } else {
              // Fallback: just position, ignore seniority
              const relaxedResult = await listOpportunities({ jobPositionIds, pageSize: 50 });
              setRecommended(relaxedResult.items || []);
            }
          } catch (err) {
            console.error("Failed to load recommended opportunities:", err);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load profile for recommendations:", err);
      })
      .finally(() => setLoadingRecommended(false));
  }, []);

  // IDs of recommended jobs (for excluding from main listing)
  const recommendedIds = useMemo(() => new Set(recommended.map(j => j.id)), [recommended]);

  return (
    <div className="max-w-[1440px] mx-auto w-full relative">
      {/* Banner: complete seu perfil */}
      {profileIncomplete && (
        <div
          className="rounded-2xl p-4 flex items-center gap-3 mb-6"
          style={{ background: 'linear-gradient(135deg, #edfbf4 0%, #d4f5e9 100%)', border: '1px solid #b0fcea' }}
        >
          <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#00DCA4' }}>
            <User className="w-5 h-5" style={{ color: '#003326' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: '#003326' }}>Complete seu perfil</p>
            <p className="text-xs" style={{ color: '#646C6F' }}>Preencha seus dados para ver oportunidades mais relevantes.</p>
          </div>
          <Link
            to="/ProfileSettings"
            className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold no-underline"
            style={{ background: '#00DCA4', color: '#003326' }}
          >
            Completar
          </Link>
        </div>
      )}

      {/* Header */}
      <h1 className="text-xl md:text-2xl font-bold mb-1" style={{ color: "#2E3233" }}>
        Vagas
      </h1>
      <p className="text-sm" style={{ color: "#646C6F" }}>
        Veja as oportunidades que separamos para você
      </p>

      <div className="h-6" />

      {/* Recommended section */}
      {loadingRecommended && !profileIncomplete && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <Star className="w-5 h-5" style={{ color: "#F5A623" }} />
            <h2 className="text-base font-bold" style={{ color: "#2E3233" }}>Recomendadas para você</h2>
          </div>
          <p className="text-sm mb-4" style={{ color: "#646C6F" }}>Baseado nas suas preferências de perfil</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={`rec-skel-${i}`} />)}
          </div>
          <div className="mt-8 border-t" style={{ borderColor: "#E5E9EA" }} />
        </div>
      )}

      {!loadingRecommended && recommended.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <Star className="w-5 h-5" style={{ color: "#F5A623" }} />
            <h2 className="text-base font-bold" style={{ color: "#2E3233" }}>Recomendadas para você</h2>
          </div>
          <p className="text-sm mb-4" style={{ color: "#646C6F" }}>Baseado nas suas preferências de perfil</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(showAllRecommended ? recommended : recommended.slice(0, RECOMMENDED_INITIAL)).map(job => (
              <JobCard key={`rec-${job.id}`} job={job} />
            ))}
          </div>
          {recommended.length > RECOMMENDED_INITIAL && (
            <div className="flex justify-center mt-4">
              <button
                onClick={() => setShowAllRecommended(v => !v)}
                className="flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-medium transition-colors"
                style={{ border: "1px solid #E5E9EA", color: "#00664C" }}
              >
                {showAllRecommended ? "Ver menos" : `Ver mais (${recommended.length - RECOMMENDED_INITIAL})`}
                <ChevronRight className="w-4 h-4" style={{ transform: showAllRecommended ? "rotate(-90deg)" : "rotate(90deg)", transition: "transform 0.2s" }} />
              </button>
            </div>
          )}
          <div className="mt-8 border-t" style={{ borderColor: "#E5E9EA" }} />
        </div>
      )}

      {/* Counter + Search + Filters button */}
      <div className="flex flex-col-reverse gap-4 items-center justify-between w-full mb-6 md:flex-row">
        {/* Counter */}
        <div className="flex items-center gap-2 w-full md:w-fit whitespace-nowrap">
          {!loading && (
            <>
              <span className="text-base font-bold" style={{ color: "#00664C" }}>{data.total}</span>
              <span className="text-base" style={{ color: "#2E3233" }}>
                {data.total !== 1 ? "resultados encontrados" : "resultado encontrado"}
              </span>
            </>
          )}
        </div>

        {/* Search + Filters button */}
        <div className="flex flex-1 flex-col md:flex-row items-center justify-end gap-2 w-full md:w-auto">
          <div className="w-full md:max-w-[350px]">
            <div className="rounded-3xl bg-white flex items-center gap-2 px-4 py-2.5"
              style={{ border: "1px solid #e2e8f0" }}>
              <Search className="w-4 h-4 flex-shrink-0" style={{ color: "#00664C" }} />
              <input
                type="text"
                placeholder="Buscar vagas por nome ou empresa"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="flex-1 text-sm bg-transparent outline-none"
                style={{ color: "#2E3233" }}
              />
              {search && (
                <button onClick={() => setSearch("")}>
                  <X className="w-3.5 h-3.5" style={{ color: "#C8CED0" }} />
                </button>
              )}
            </div>
          </div>

          <button
            onClick={() => setShowFilters(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap w-full md:w-auto justify-center transition-colors"
            style={{
              border: filtersCount > 0 ? "1px solid #00664C" : "1px solid #E5E9EA",
              background: filtersCount > 0 ? "#EAFAF6" : "transparent",
              color: filtersCount > 0 ? "#00664C" : "#646C6F",
            }}
          >
            <SlidersHorizontal className="w-4 h-4" />
            {filterOptionsLoading ? "Filtros" : `Filtros${filtersCount > 0 ? ` (${String(filtersCount).padStart(2, "0")})` : ""}`}
          </button>
        </div>
      </div>

      {/* Grid of cards */}
      {(() => {
        const mainItems = recommendedIds.size > 0
          ? data.items.filter(job => !recommendedIds.has(job.id))
          : data.items;
        return loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : mainItems.length === 0 ? (
          <div className="w-full flex justify-center items-center flex-col min-h-[400px] text-center">
            <FileText className="w-16 h-16 mb-4" style={{ color: "#E5E9EA" }} />
            <h4 className="text-base font-bold mb-2" style={{ color: "#2E3233" }}>
              {hasActiveFilters ? "Não há informações" : "Ainda não há informações"}
            </h4>
            <p className="text-sm max-w-[360px]" style={{ color: "#646C6F" }}>
              {hasActiveFilters
                ? "Nenhuma vaga encontrada com os filtros ou palavras-chave selecionados. Experimente ajustar os filtros e tente novamente!"
                : "No momento, não há vagas disponíveis para visualização."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {mainItems.map(job => <JobCard key={job.id} job={job} />)}
          </div>
        );
      })()}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors"
            style={{
              border: "1px solid #E5E9EA",
              color: page === 1 ? "#C8CED0" : "#00664C",
              cursor: page === 1 ? "default" : "pointer",
            }}
          >
            <ChevronLeft className="w-4 h-4" /> Anterior
          </button>
          <span className="text-sm" style={{ color: "#646C6F" }}>
            Página {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors"
            style={{
              border: "1px solid #E5E9EA",
              color: page === totalPages ? "#C8CED0" : "#00664C",
              cursor: page === totalPages ? "default" : "pointer",
            }}
          >
            Próxima <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="h-16" />

      {/* Filter Side Menu */}
      <FilterSideMenu
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filterOptions={filterOptions}
        filters={filters}
        onApply={handleApplyFilters}
      />
    </div>
  );
}
